import axios from 'axios'
import isArray from 'lodash/isArray'
import has from 'lodash/has'
import {removeToken,removeMarsToken} from '@/utils/auth'

import { showLoadingToast, closeToast, showToast } from 'vant';

const getErrorMessage = (error: any): string | null => {
  const response = error?.response
  const status = response?.status
  const responseData = response?.data

  if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return null
  if (status === 504 || status === 408 || error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
    return '请求超时，请稍后重试'
  }
  if (status === 502 || status === 503) return '服务暂时不可用，请稍后重试'
  if (status >= 500) return '服务异常，请稍后重试'
  if (!response && (error?.code === 'ERR_NETWORK' || error?.request || error?.message === 'Network Error')) {
    return '网络连接异常，请检查网络后重试'
  }

  const details = response?.code == 500 && Array.isArray(response?.errorData)
    ? response.errorData.map(item => item?.error).filter(message => typeof message === 'string' && message.trim())
    : []
  const message = details.length
    ? details.join(',')
    : typeof responseData === 'string'
      ? responseData
      : responseData?.error?.message || responseData?.message || responseData?.msg

  if (message === '认证信息不存在') return null
  if (typeof message !== 'string' || !message.trim()) return '请求失败，请稍后重试'
  // 网关可能返回 HTML 错误页，不能将整页内容直接作为提示。
  if (/<!doctype\b|<\/?[a-z][^>]*>/i.test(message)) return '服务响应异常，请稍后重试'
  return message
}

const createInstance = () => {
  const instance = axios.create({
    timeout: 1000*60*2,
    withCredentials: true,
    headers: {
      'x-port-id': 1,
    }
  })

  instance.interceptors.request.use(config => {
    if (config.url && !config.url.startsWith('/api/in/kol')) {
      config.url = '/api' + config.url
    }
    return config
  })

  // 让使用者拿到数据而非原始响应
  instance.interceptors.response.use(
    response => {
      return response
    },
    error => {
      return Promise.reject(error)
    },
  )
  return instance
}

const createApiClient = () => ({
  instance: createInstance(),
  errorMiddleware: null,
  retryUrl: null,
  promiseCache: {},
  get(url: string, options = {}): Promise<any> {
    console.log('options', options);
    return this.request('get', url, null, {}, options)
  },
  put(url: string, data?: any, options = {}): Promise<any> {
    return this.request('put', url, data, options)
  },
  post(url: string, data = {}, options = {}): Promise<any> {
    return this.request('post', url, data, options)
  },
  patch(url: string, data: any, options = {}): Promise<any> {
    return this.request('patch', url, data, options)
  },
  delete(url: string, data: any = {}, options = {}): Promise<any> {
    return this.request('delete', url, data, options)
  },
  upload(url: string, data: any, files: any[], options = {}): Promise<any> {
    if (isArray(files)) {
      files = files.map((file: any) => ({name: 'source', data: file}))
    } else {
      files = [{name: 'source', data: files}]
    }

    const formData = new FormData()
    files.forEach(file => formData.append(file.name, file.data))
    data.forEach((value: any, key: string) => formData.append(key, value))

    return this.post(url, formData, {...options, forbidReuse: true, timeout: 60000})
  },
  request(method: string, url: string, data: any, options = {}, params?:any): Promise<any> {
    showLoadingToast({
      // message: '加载中...',
      duration: 0,
      forbidClick: true,
    });
    const param: any = {method, url, data, ...options, params}
    const promise: Promise<any> = this.instance
      .request(param)
      .then(response => {
        closeToast();
        console.log('then-111', url, response);
        
        if (has(response.data, 'code') && response.data.code !== 0&&response.data.code !== 200) {
          return Promise.reject({response: {data: response.data.message || response.data.msg, code: response.data.code, errorData: response.data.data}})
        } else {
          return this.handleSuccess(url, method, response)
        }
      })
      .catch(error => {
        closeToast();
        console.log('catch-222',url,error);
        return this.handleError(url, method, data, options, error)
      })

    return promise
  },
  handleError(url: string, method: string, data: any, options: any, error: any): Promise<never> {
    const responseStatus = error?.response?.status
    const message = getErrorMessage(error)
    if (message) showToast(message)
    if (responseStatus==401 && !location.href.includes('/login')) {
      removeToken()
      removeMarsToken()
      location.replace('/login')
    }
    // 保留失败状态，避免调用方在请求失败后继续执行成功逻辑。
    return Promise.reject(error)
  },
  handleSuccess(url: string, method: string, response: any): Promise<any> {
    console.log('response', response)
    if (response.data && response.data.message && response.data.message === 'success') {
      return Promise.resolve(response.data.data)
    } else {
      console.log('success', response.data)
      return Promise.resolve(response.data)
    }
  },
  // 设置参数
  setBaseUrl(baseURL: string): void {
    this.instance.defaults.baseURL = baseURL
  },
  setToken(token: string | null): void {
    this.instance.defaults.headers.common['Authorization'] = token
  },
  delToken(): void {
    this.instance.defaults.headers.common['Authorization'] = ''
  },
})

const ApiClient = createApiClient()
ApiClient.setBaseUrl(process.env.VUE_APP_BASE_URL as string)

// 创建一个不需要token的http请求
export const noTokenApiClient = createApiClient()
noTokenApiClient.instance.interceptors.request.use(
  function (config) {
    // 在发送请求之前做些什么
    return config
  },
  function (error) {
    // 对请求错误做些什么
    return Promise.reject(error)
  },
)

export default ApiClient
